import React, { useState, useEffect } from 'react'
import Wrapper from '../../components/Wrapper'
import { useNavigate } from 'react-router-dom'
import {
  Paper,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import KeyIcon from '@mui/icons-material/Key'
import TerminalIcon from '@mui/icons-material/Terminal'
import WebhookIcon from '@mui/icons-material/Webhook'
import { shortFunction } from '../../translate'
import { toast } from 'react-toastify'
import { greenTheme, inputStyles, inputLabelStyles, sectionTitleStyles, buttonStyles } from '../../helpers/style'
import Api from '../../Api'
import Cookies from 'js-cookie'

export default function CallCenterSettings() {
  const navigate = useNavigate()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [callCenterId, setCallCenterId] = useState(null)
  const [form, setForm] = useState({
    key: '',
    command: '',
    webhook: '',
  })

  const hasExistingConfig = !!callCenterId
  const hideKeys = !!(Cookies.get('view') || localStorage.getItem('access_token_urs'))
  const displayValue = (val) => (
    hasExistingConfig && hideKeys && val
      ? '•'.repeat(Math.min(String(val).length, 12))
      : val
  )

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const { data } = await Api.callCenterGet()
        if (data?.callCenter) {
          const cc = data.callCenter
          setCallCenterId(cc.id ?? null)
          setForm({
            key: cc.key ?? cc.callcenter_key ?? '',
            command: cc.command ?? cc.domain ?? '',
            webhook: cc.webhook ?? '',
          })
        }
      } catch (e) {
        // No existing config or not connected
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await Api.callCenterCreate({
        domain: form.command,
        key: form.key,
        webhook: form.webhook,
      })
      toast.success('Saved')
      navigate(-1)
    } catch (e) {
      toast.error(shortFunction.error?.[langIndex] || 'Error')
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!callCenterId) return
    const msg = langIndex === 2 ? 'Удалить настройки колл-центра?' : langIndex === 3 ? 'Ջնջե՞լ զանգերի կենտրոնի կարգավորումները' : 'Delete call center settings?'
    if (!window.confirm(msg)) return
    setDeleting(true)
    try {
      await Api.callCenterDelete(callCenterId)
      toast.success(langIndex === 2 ? 'Удалено' : langIndex === 3 ? 'Ջնջվեց' : 'Deleted')
      setCallCenterId(null)
      setForm({ key: '', command: '', webhook: '' })
    } catch (e) {
      toast.error(shortFunction.error?.[langIndex] || 'Error')
      console.error(e)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
            <IconButton
              size="small"
              sx={{ color: greenTheme.primary, fontSize: 18 }}
              onClick={handleGoBack}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {shortFunction.callCenterSettingsTitle[langIndex]}
            </Typography>
          </Box>

          <Paper sx={{ p: 3, border: `1px solid ${greenTheme.primary}` }}>
            {/* Call center key */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={sectionTitleStyles}>
                <KeyIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 22, color: greenTheme.primary }} />
                {shortFunction.callCenterKeySection[langIndex]}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label={shortFunction.callCenterKeySection[langIndex]}
                placeholder={shortFunction.callCenterKeyPlaceholder[langIndex]}
                value={displayValue(form.key)}
                onChange={(e) => setForm((prev) => ({ ...prev, key: e.target.value }))}
                variant="outlined"
                sx={inputStyles}
                InputLabelProps={{ sx: inputLabelStyles }}
                disabled={hasExistingConfig}
              />
            </Box>

            {/* Command */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={sectionTitleStyles}>
                <TerminalIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 22, color: greenTheme.primary }} />
                {shortFunction.callCenterCommandSection[langIndex]}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label={shortFunction.callCenterCommandSection[langIndex]}
                placeholder={shortFunction.callCenterCommandPlaceholder[langIndex]}
                value={displayValue(form.command)}
                onChange={(e) => setForm((prev) => ({ ...prev, command: e.target.value }))}
                variant="outlined"
                sx={inputStyles}
                InputLabelProps={{ sx: inputLabelStyles }}
                disabled={hasExistingConfig}
              />
            </Box>

            {/* Webhook */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={sectionTitleStyles}>
                <WebhookIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 22, color: greenTheme.primary }} />
                {shortFunction.callCenterWebhookSection[langIndex]}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label={shortFunction.callCenterWebhookSection[langIndex]}
                placeholder={shortFunction.callCenterWebhookPlaceholder[langIndex]}
                value={displayValue(form.webhook)}
                onChange={(e) => setForm((prev) => ({ ...prev, webhook: e.target.value }))}
                variant="outlined"
                sx={inputStyles}
                InputLabelProps={{ sx: inputLabelStyles }}
                disabled={hasExistingConfig}
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {hasExistingConfig && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting || loading}
                  sx={{
                    ...buttonStyles,
                    backgroundColor: '#d32f2f !important',
                    color: '#fff !important',
                    '&:hover': { backgroundColor: '#b71c1c !important', color: '#fff !important' },
                    '&:disabled': { backgroundColor: '#ef9a9a !important', color: '#fff !important' },
                  }}
                >
                  {deleting ? (shortFunction.operator_loading?.[langIndex] || 'Deleting...') : (langIndex === 2 ? 'Удалить' : langIndex === 3 ? 'Ջնջել' : 'Delete')}
                </Button>
              )}
              {!hasExistingConfig && (
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={handleSave}
                  disabled={saving || loading}
                  sx={{
                    ...buttonStyles,
                    backgroundColor: '#7ebc0a !important',
                    color: '#fff !important',
                    '&:hover': {
                      backgroundColor: '#6aa309 !important',
                      color: '#fff !important',
                    },
                    '&:disabled': { backgroundColor: '#a0c97a !important', color: '#fff !important' },
                  }}
                >
                  {saving ? (shortFunction.operator_loading?.[langIndex] || 'Saving...') : (shortFunction.operator_save?.[langIndex] || 'Save')}
                </Button>
              )}
            </Box>
          </Paper>
        </Paper>
      </div>
    </Wrapper>
  )
}
